import React, { useState, useEffect, useMemo } from 'react';
import { User, Reservation, ReservationStatus, Gym, Slot, Zone } from '../types';
import { WEEKLY_PASS_LIMIT } from '../constants';
import { supabase } from '../supabase';
import { QRCodeCanvas } from 'qrcode.react';
import { MapPin, Search, Calendar, ChevronRight, QrCode, Info, CheckCircle, Ticket, Loader2, Building2, X, History, Filter, MessageSquare } from 'lucide-react';
import Chat from './Chat';
import LegalFooter from './LegalFooter';

interface ClientPortalProps {
  user: User;
  reservations: Reservation[];
  setReservations: React.Dispatch<React.SetStateAction<Reservation[]>>;
  gyms: Gym[];
  zones: Zone[];
}

const DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const toISODate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ClientPortal: React.FC<ClientPortalProps> = ({ user, reservations, setReservations, gyms, zones }) => {
  const [activeTab, setActiveTab] = useState<'explore' | 'my-bookings' | 'chat'>('explore');
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedChatGym, setSelectedChatGym] = useState<Gym | null>(null);
  const [unreadGymIds, setUnreadGymIds] = useState<Set<string>>(new Set());
  const [chatConversations, setChatConversations] = useState<{ id: string, name: string }[]>([]);
  const [showNewChatList, setShowNewChatList] = useState(false);

  const [realSlots, setRealSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState<string>(toISODate(new Date()));
  const [slotRemaining, setSlotRemaining] = useState<Record<string, number>>({});

  const todayStr = useMemo(() => toISODate(new Date()), []);

  const filteredChatConversations = useMemo(() => {
    return chatConversations.filter(c =>
      c.name.toLowerCase().includes(chatSearchTerm.toLowerCase())
    );
  }, [chatConversations, chatSearchTerm]);

  const gymsForNewChat = useMemo(() => {
    const existingChatIds = new Set(chatConversations.map(c => c.id));
    return gyms.filter(g => !existingChatIds.has(g.id));
  }, [gyms, chatConversations]);

  const isPastSelectedDate = useMemo(() => {
    return selectedDate < todayStr;
  }, [selectedDate, todayStr]);

  const selectedDateDayOfWeek = useMemo(() => {
    const d = new Date(selectedDate + 'T00:00:00');
    return d.getDay();
  }, [selectedDate]);

  const getStartOfCurrentWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(now.setDate(diff));
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  const startOfWeek = getStartOfCurrentWeek();
  const homeGym = gyms.find(g => g.id === user.homeGymId);
  const allUserReservations = reservations.filter(r => r.clientId === user.id);

  const currentWeekReservations = allUserReservations.filter(r => {
    const resDate = new Date(r.date + 'T00:00:00');
    return r.status !== ReservationStatus.CANCELLED && resDate >= startOfWeek;
  });

  const usedPasses = currentWeekReservations.length;
  const remainingPasses = Math.max(0, WEEKLY_PASS_LIMIT - usedPasses);

  useEffect(() => {
    if (selectedGym) {
      fetchGymSlotsAndAvailability(selectedGym.id, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGym, selectedDate]);

  useEffect(() => {
    if (!user.id) return;
    fetchData();

    const channel = supabase
      .channel('client_unread_messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `client_id=eq.${user.id}`,
        },
        () => {
          fetchUnreadMessages();
          if (activeTab === 'chat') fetchChatConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user.id, activeTab]);

  const fetchData = async () => {
    setLoadingSlots(true);
    try {
      await fetchUnreadMessages();
      if (activeTab === 'chat') await fetchChatConversations();
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('gym_id')
        .eq('client_id', user.id)
        .neq('is_read', true)
        .eq('sender_role', 'GYM');

      if (error) throw error;

      const gymIds = new Set((data || []).map(m => m.gym_id));
      setUnreadGymIds(gymIds);
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  };

  const fetchChatConversations = async () => {
    try {
      const { data: msgData, error: msgError } = await supabase
        .from('messages')
        .select('gym_id')
        .eq('client_id', user.id);

      if (msgError) throw msgError;

      const uniqueGymIds = Array.from(new Set((msgData || []).map(m => m.gym_id)));

      if (uniqueGymIds.length === 0) {
        setChatConversations([]);
        return;
      }

      const { data: gymData, error: gymError } = await supabase
        .from('gyms')
        .select('id, name')
        .in('id', uniqueGymIds);

      if (gymError) throw gymError;

      setChatConversations((gymData || []).map(g => ({
        id: g.id,
        name: g.name
      })));
    } catch (err) {
      console.error("Error fetching chat conversations:", err);
    }
  };

  const fetchGymSlotsAndAvailability = async (gymId: string, dateISO: string) => {
    setLoadingSlots(true);
    try {
      const { data: slotsData, error: slotsErr } = await supabase
        .from('slots')
        .select('*')
        .eq('gym_id', gymId)
        .eq('is_active', true)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (slotsErr) throw slotsErr;

      const slotsMapped: Slot[] = (slotsData || []).map(d => ({
        id: d.id,
        gymId: d.gym_id,
        dayOfWeek: d.day_of_week,
        startTime: d.start_time.slice(0, 5),
        endTime: d.end_time.slice(0, 5),
        capacity: d.capacity,
        isActive: d.is_active
      }));

      setRealSlots(slotsMapped);

      if (slotsMapped.length > 0) {
        const slotIds = slotsMapped.map(s => s.id);

        const { data: resData, error: resErr } = await supabase
          .from('reservations')
          .select('slot_id, status')
          .eq('gym_id', gymId)
          .eq('reservation_date', dateISO)
          .in('slot_id', slotIds);

        if (resErr) throw resErr;

        const takenBySlot: Record<string, number> = {};
        (resData || []).forEach(r => {
          if (r.status !== ReservationStatus.CANCELLED && r.slot_id) {
            takenBySlot[r.slot_id] = (takenBySlot[r.slot_id] || 0) + 1;
          }
        });

        const remainingMap: Record<string, number> = {};
        slotsMapped.forEach(s => {
          const taken = takenBySlot[s.id] || 0;
          remainingMap[s.id] = Math.max(0, (s.capacity || 0) - taken);
        });

        setSlotRemaining(remainingMap);
      } else {
        setSlotRemaining({});
      }
    } catch (err) {
      console.error("Error loading slots:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const availableGyms = gyms.filter(g => {
    const isDifferentZone = g.zoneId !== user.homeZoneId;
    const isNotMyGym = g.id !== user.homeGymId;
    const matchesSearch = g.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSelectedZone = selectedZoneId ? g.zoneId === selectedZoneId : true;
    return isDifferentZone && isNotMyGym && matchesSearch && matchesSelectedZone;
  });

  const handleBooking = async (slot: Slot) => {
    if (remainingPasses <= 0) {
      alert("Has agotado tus créditos semanales.");
      return;
    }

    if (isPastSelectedDate) {
      alert("No puedes reservar una fecha pasada.");
      return;
    }

    if (slot.dayOfWeek !== selectedDateDayOfWeek) {
      alert("Este bloque no corresponde al día seleccionado.");
      return;
    }

    const remaining = slotRemaining[slot.id] ?? slot.capacity ?? 0;
    if (remaining <= 0) {
      alert("Este horario ya no tiene cupos disponibles.");
      return;
    }

    const { data, error } = await supabase.rpc('book_reservation', {
      p_client_id: String(user.id),
      p_gym_id: String(slot.gymId),
      p_slot_id: String(slot.id),
      p_date: selectedDate
    });

    console.log("RPC book_reservation:", { data, error });

    if (error) {
      alert(error.message);
      return;
    }

    if (data) {
      const r = Array.isArray(data) ? data[0] : data;

      setReservations(prev => [
        ...prev,
        {
          id: r.id,
          clientId: r.client_id,
          gymId: r.gym_id,
          slotId: r.slot_id,
          date: r.reservation_date,
          status: r.status,
          qrCode: r.qr_code,
          createdAt: r.created_at
        }
      ]);

      setSlotRemaining(prev => ({
        ...prev,
        [slot.id]: Math.max(0, (prev[slot.id] ?? slot.capacity ?? 0) - 1)
      }));

      setSelectedGym(null);
      setActiveTab('my-bookings');
    } else {
      alert("No se recibió respuesta de la base.");
    }
  };

  const executeCancel = async (resId: string) => {
    const resBefore = reservations.find(r => r.id === resId);
    const oldSlotId = resBefore?.slotId ?? null;

    const { error } = await supabase
      .from('reservations')
      .update({
        status: ReservationStatus.CANCELLED,
        slot_id: null
      })
      .eq('id', resId);

    if (!error) {
      setReservations(prev =>
        prev.map(r =>
          r.id === resId ? { ...r, status: ReservationStatus.CANCELLED, slotId: null } : r
        )
      );
    }

    if (
      !error &&
      resBefore &&
      oldSlotId &&
      resBefore.status === ReservationStatus.CONFIRMED &&
      selectedGym &&
      resBefore.gymId === selectedGym.id &&
      resBefore.date === selectedDate
    ) {
      setSlotRemaining(prev => ({
        ...prev,
        [oldSlotId]: (prev[oldSlotId] ?? 0) + 1
      }));
    }

    setConfirmCancelId(null);
  };

  const userZoneName = zones.find(z => z.id === user.homeZoneId)?.name || 'Tu zona';

  const visibleSlots = useMemo(() => {
    return realSlots.filter(s => s.dayOfWeek === selectedDateDayOfWeek);
  }, [realSlots, selectedDateDayOfWeek]);

  return (
    <div className="space-y-8 max-w-2xl mx-auto pb-12">
      <div className="glass-card p-6 sm:p-8 flex items-center justify-between relative overflow-hidden group border-brand/20">
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
          <Building2 size={80} />
        </div>
        <div className="flex items-center gap-4 sm:gap-6 relative z-10">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-brand rounded-full flex items-center justify-center text-black shadow-[0_0_20px_rgba(255,255,255,0.2)]">
            <Building2 size={24} className="sm:w-8 sm:h-8" />
          </div>
          <div>
            <p className="text-[8px] sm:text-[10px] font-bold text-brand uppercase tracking-[0.3em] leading-none mb-2">Home Base</p>
            <h2 className="text-xl sm:text-3xl font-display uppercase tracking-tight text-white">{homeGym?.name || 'Sede base'}</h2>
            <p className="text-brand text-[9px] sm:text-[10px] font-display uppercase tracking-[0.2em] mt-1 italic opacity-80">be a bro</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[8px] sm:text-[9px] font-bold bg-white/5 text-white/60 px-2 py-1 rounded border border-white/5 uppercase tracking-widest">
                {userZoneName}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right flex items-center gap-4 relative z-10">
          {unreadGymIds.size > 0 && (
            <div className="glass-card px-4 py-2 flex flex-col items-center border-emerald-500/20 bg-emerald-500/5 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
              <p className="text-[7px] uppercase font-bold text-emerald-500/60 tracking-[0.2em] mb-0.5">Mensajes</p>
              <p className="font-display text-xl text-emerald-500">{unreadGymIds.size}</p>
            </div>
          )}
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-3xl sm:text-5xl font-display text-brand leading-none">{remainingPasses}</span>
              <span className="text-white/20 font-light text-xl sm:text-2xl">/</span>
              <span className="text-sm sm:text-lg font-display text-white/40">{WEEKLY_PASS_LIMIT}</span>
            </div>
            <p className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase mt-2 tracking-[0.2em]">Pases</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 bg-white/5 p-1 rounded-xl border border-white/5">
        <button
          onClick={() => { setActiveTab('explore'); setSelectedGym(null); setSelectedChatGym(null); }}
          className={`flex-1 py-4 rounded-lg text-[10px] font-display uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'explore' ? 'bg-brand text-black' : 'text-white/40 hover:bg-white/5'}`}
        >
          <Search size={16} /> Explorar
        </button>
        <button
          onClick={() => { setActiveTab('my-bookings'); setSelectedGym(null); setSelectedChatGym(null); }}
          className={`flex-1 py-4 rounded-lg text-[10px] font-display uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'my-bookings' ? 'bg-brand text-black' : 'text-white/40 hover:bg-white/5'}`}
        >
          <Ticket size={16} /> Reservas
        </button>
        <button
          onClick={() => { setActiveTab('chat'); setSelectedGym(null); setSelectedChatGym(null); }}
          className={`flex-1 py-4 rounded-lg text-[10px] font-display uppercase tracking-widest flex items-center justify-center gap-2 transition-all relative ${activeTab === 'chat' ? 'bg-brand text-black' : 'text-white/40 hover:bg-white/5'}`}
        >
          <MessageSquare size={16} /> Mensajes
          {unreadGymIds.size > 0 && (
            <span className="absolute top-2 right-2 w-3 h-3 bg-emerald-500 rounded-full border-2 border-dark-bg shadow-[0_0_10px_rgba(16,185,129,0.8)]"></span>
          )}
        </button>
      </div>

      {activeTab === 'explore' && !selectedGym && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/20" size={20} />
            <input
              type="text"
              placeholder="BUSCAR SEDE POR NOMBRE..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="luxury-input w-full pl-14 py-5 font-display uppercase tracking-widest text-sm"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 ml-1">
              <Filter size={14} className="text-brand" />
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-[0.2em]">Zonas Disponibles</span>
            </div>
            <div className="relative">
              <select
                value={selectedZoneId || ''}
                onChange={(e) => setSelectedZoneId(e.target.value || null)}
                className="luxury-input w-full py-4 pl-5 pr-12 font-display uppercase tracking-widest text-sm appearance-none cursor-pointer"
              >
                <option value="" className="bg-dark-surface text-white">Todas las Zonas</option>
                {zones.filter(z => z.id !== user.homeZoneId).map(zone => (
                  <option key={zone.id} value={zone.id} className="bg-dark-surface text-white">
                    {zone.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-brand">
                <Filter size={18} />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 bg-brand/5 p-6 border border-brand/10">
            <Info size={18} className="text-brand shrink-0 mt-0.5" />
            <p className="text-[10px] text-white/60 font-bold uppercase tracking-wider leading-relaxed">
              ESTÁS EN LA ZONA <b className="text-brand">{userZoneName}</b>. EL SISTEMA OCULTA LAS SEDES DE TU PROPIA ZONA PARA CUMPLIR CON LAS NORMAS DE LA RED ELITE.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6">
            {availableGyms.length === 0 ? (
              <div className="p-10 sm:p-20 text-center glass-card border-dashed">
                <Building2 size={48} className="mx-auto text-white/5 mb-6" />
                <p className="text-white/20 font-display uppercase tracking-widest">No hay sedes disponibles</p>
              </div>
            ) : (
              availableGyms.map(gym => (
                <button
                  key={gym.id}
                  onClick={() => setSelectedGym(gym)}
                  className="group glass-card overflow-hidden text-left hover:border-brand/50 transition-all active:scale-[0.99] flex flex-col"
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img src={gym.image} alt={gym.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 grayscale group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-transparent to-transparent opacity-60"></div>
                    <div className="absolute top-4 left-4 bg-brand px-3 py-1 rounded-none flex items-center gap-2 shadow-xl">
                      <MapPin size={10} className="text-black" />
                      <span className="text-[9px] font-display uppercase text-black tracking-widest">
                        {zones.find(z => z.id === gym.zoneId)?.name || 'Zona Red'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 sm:p-8 flex justify-between items-center bg-dark-surface/80">
                    <div>
                      <h3 className="font-display text-xl sm:text-2xl uppercase tracking-tight text-white group-hover:text-brand transition-colors">{gym.name}</h3>
                      <p className="text-[9px] sm:text-[10px] text-white/40 font-bold uppercase tracking-widest mt-1">{gym.address}</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/5 group-hover:bg-brand rounded-full flex items-center justify-center text-white/20 group-hover:text-black transition-all">
                      <ChevronRight size={20} className="sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'explore' && selectedGym && (
        <div className="glass-card overflow-hidden border-white/5 animate-in fade-in slide-in-from-bottom-8">
          <div className="relative h-56 sm:h-72">
            <img src={selectedGym.image} alt={selectedGym.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-transparent to-transparent"></div>
            <button
              onClick={() => { setSelectedGym(null); setSelectedChatGym(null); }}
              className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-dark-surface/80 backdrop-blur-md p-3 sm:p-4 rounded-xl border border-white/10 hover:bg-brand hover:text-black transition-all active:scale-90"
            >
              <ChevronRight size={18} className="rotate-180 sm:w-5 sm:h-5" />
            </button>
            <div className="absolute bottom-6 left-6 right-6 sm:bottom-8 sm:left-8 sm:right-8 flex justify-between items-end">
              <div>
                <span className="bg-brand text-black text-[8px] sm:text-[9px] font-display px-3 py-1.5 rounded-none uppercase tracking-widest shadow-xl">
                  {zones.find(z => z.id === selectedGym.zoneId)?.name}
                </span>
                <h2 className="text-3xl sm:text-5xl font-display uppercase tracking-tighter text-white mt-3 sm:mt-4 leading-none">{selectedGym.name}</h2>
                <div className="flex items-center gap-2 mt-2 sm:mt-3">
                  <MapPin size={12} className="text-brand sm:w-4 sm:h-4" />
                  <p className="text-[9px] sm:text-[11px] text-white/60 font-bold uppercase tracking-widest">{selectedGym.address}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedChatGym(selectedChatGym ? null : selectedGym)}
                className={`p-4 rounded-2xl border transition-all relative ${selectedChatGym ? 'bg-brand text-black border-brand' : 'bg-dark-surface/80 text-brand border-white/10 hover:bg-white/5'}`}
              >
                <MessageSquare size={24} />
                {!selectedChatGym && unreadGymIds.has(selectedGym.id) && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-dark-bg animate-pulse"></span>
                )}
              </button>
            </div>
          </div>

          <div className="p-6 sm:p-10 space-y-8 sm:space-y-10">
            {selectedChatGym ? (
              <Chat
                currentUser={user}
                targetId={selectedChatGym.id}
                targetName={selectedChatGym.name}
              />
            ) : (
              <>
                <p className="text-xs sm:text-sm text-white/40 leading-relaxed italic border-l-2 border-brand/20 pl-4 sm:pl-6">"{selectedGym.description}"</p>

                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3">
                    <Calendar size={16} className="text-brand sm:w-[18px] sm:h-[18px]" />
                    <h3 className="font-display uppercase tracking-wider text-xs sm:text-sm">Selecciona Fecha</h3>
                  </div>
                  <input
                    type="date"
                    value={selectedDate}
                    min={todayStr}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="luxury-input w-full py-3 sm:py-4 font-mono text-xs sm:text-sm"
                  />
                  {isPastSelectedDate && (
                    <p className="text-[9px] sm:text-[10px] text-red-400 font-bold uppercase tracking-widest">Fecha no válida</p>
                  )}
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-3">
                    <History size={16} className="text-brand sm:w-[18px] sm:h-[18px]" />
                    <h3 className="font-display uppercase tracking-wider text-xs sm:text-sm">
                      Bloques: {selectedDate.split('-').reverse().join('/')}
                    </h3>
                  </div>

                  <div className="grid gap-3 sm:gap-4">
                    {loadingSlots ? (
                      <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-brand" /></div>
                    ) : visibleSlots.length === 0 ? (
                      <div className="p-10 sm:p-12 text-center bg-white/5 rounded-2xl text-white/20 uppercase tracking-widest text-[9px] sm:text-[10px] font-bold">
                        Sin horarios para este día
                      </div>
                    ) : (
                      visibleSlots.map(slot => {
                        const remaining = slotRemaining[slot.id] ?? slot.capacity ?? 0;
                        const isFull = remaining <= 0;

                        return (
                          <div
                            key={slot.id}
                            className="flex items-center justify-between p-4 sm:p-6 bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition-all group"
                          >
                            <div className="flex items-center gap-4 sm:gap-6">
                              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/5 rounded-xl flex flex-col items-center justify-center font-display text-[10px] sm:text-xs text-brand border border-white/5 group-hover:border-brand/30 transition-colors">
                                {DAYS[slot.dayOfWeek].slice(0, 3).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-lg sm:text-2xl font-display uppercase tracking-tight text-white">{slot.startTime} - {slot.endTime}</p>
                                <div className="flex items-center gap-2 mt-1 sm:mt-2">
                                  <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${isFull ? 'bg-red-500' : 'bg-brand shadow-[0_0_8px_rgba(255,255,255,0.4)]'}`}></div>
                                  <p className="text-[8px] sm:text-[9px] font-bold text-white/30 uppercase tracking-[0.2em]">
                                    Cupos: {remaining}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {(() => {
                              const now = new Date();
                              const [h, m] = slot.endTime.split(':').map(Number);
                              const isToday = selectedDate === todayStr;
                              const hasEnded = isToday && (now.getHours() > h || (now.getHours() === h && now.getMinutes() > m));
                              const canBook = !isPastSelectedDate && !hasEnded && !isFull && remainingPasses > 0;

                              return (
                                <button
                                  onClick={() => handleBooking(slot)}
                                  disabled={!canBook}
                                  className={`px-4 sm:px-8 py-3 sm:py-4 text-[9px] sm:text-[10px] font-display uppercase tracking-widest transition-all ${canBook ? 'bg-brand text-black hover:scale-105 active:scale-95' : 'bg-white/5 text-white/20 cursor-not-allowed'}`}
                                >
                                  {isPastSelectedDate ? 'Pasado' : (hasEnded ? 'Fin' : (isFull ? 'Lleno' : 'Reservar'))}
                                </button>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
          {selectedChatGym ? (
            <div className="space-y-6">
              <button
                onClick={() => setSelectedChatGym(null)}
                className="flex items-center gap-2 text-white/40 hover:text-brand transition-colors uppercase text-[10px] font-bold tracking-widest"
              >
                <ChevronRight size={14} className="rotate-180" /> Volver a Mensajes
              </button>
              <Chat
                currentUser={user}
                targetId={selectedChatGym.id}
                targetName={selectedChatGym.name}
              />
            </div>
          ) : showNewChatList ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowNewChatList(false)}
                  className="flex items-center gap-2 text-white/40 hover:text-brand transition-colors uppercase text-[10px] font-bold tracking-widest"
                >
                  <ChevronRight size={14} className="rotate-180" /> Cancelar
                </button>
                <h3 className="font-display uppercase tracking-widest text-sm text-white">Nueva Conversación</h3>
              </div>

              <div className="grid gap-3">
                {gymsForNewChat.length === 0 ? (
                  <div className="glass-card p-12 text-center text-[10px] text-white/20 font-bold uppercase tracking-widest border-dashed">
                    Ya has hablado con todas las sedes
                  </div>
                ) : (
                  gymsForNewChat.map(gym => (
                    <button
                      key={gym.id}
                      onClick={() => {
                        setSelectedChatGym(gym);
                        setShowNewChatList(false);
                      }}
                      className="w-full glass-card p-4 flex items-center justify-between hover:bg-white/5 transition-all group border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand border border-brand/20">
                          <Building2 size={18} />
                        </div>
                        <div className="text-left">
                          <h4 className="font-display uppercase tracking-tight text-white group-hover:text-brand transition-colors text-sm">{gym.name}</h4>
                          <p className="text-[8px] text-white/30 font-bold uppercase tracking-widest">Iniciar chat</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-white/10 group-hover:text-brand transition-colors" />
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-brand rounded-full shadow-[0_0_8px_rgba(255,255,255,0.6)]"></div>
                  <h3 className="font-display uppercase tracking-widest text-sm text-white">Mensajería</h3>
                </div>
                <button
                  onClick={() => setShowNewChatList(true)}
                  className="bg-brand/10 text-brand border border-brand/20 px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-widest hover:bg-brand hover:text-black transition-all"
                >
                  Nuevo Chat
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={16} />
                <input
                  type="text"
                  placeholder="BUSCAR CONVERSACIÓN..."
                  value={chatSearchTerm}
                  onChange={(e) => setChatSearchTerm(e.target.value)}
                  className="luxury-input w-full pl-12 py-4 font-display uppercase tracking-widest text-[10px]"
                />
              </div>

              <div className="space-y-3">
                {filteredChatConversations.length === 0 ? (
                  <div className="glass-card p-12 text-center text-[10px] text-white/20 font-bold uppercase tracking-widest border-dashed">
                    {chatSearchTerm ? 'No se encontraron resultados' : 'No tienes chats activos'}
                  </div>
                ) : (
                  filteredChatConversations.map(gym => (
                    <button
                      key={gym.id}
                      onClick={() => {
                        const fullGym = gyms.find(g => g.id === gym.id);
                        if (fullGym) {
                          setSelectedChatGym(fullGym);
                        }
                      }}
                      className="w-full glass-card p-5 flex items-center justify-between hover:bg-white/5 transition-all group border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand border border-brand/20 group-hover:scale-110 transition-transform">
                          <Building2 size={20} />
                        </div>
                        <div className="text-left">
                          <h4 className="font-display uppercase tracking-tight text-white group-hover:text-brand transition-colors">{gym.name}</h4>
                          <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest">Chat con la sede</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {unreadGymIds.has(gym.id) && (
                          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
                        )}
                        <ChevronRight size={18} className="text-white/10 group-hover:text-brand transition-colors" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'my-bookings' && (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4">
          {allUserReservations.length === 0 ? (
            <div className="glass-card p-20 border-dashed text-center">
              <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-8 text-white/10">
                <Ticket size={48} />
              </div>
              <p className="text-white/40 font-display uppercase tracking-widest mb-10">Sin tickets activos</p>
              <button
                onClick={() => setActiveTab('explore')}
                className="brutal-btn"
              >
                Explorar la Red
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="space-y-6">
                <div className="flex items-center gap-3 ml-2">
                  <div className="w-2 h-2 bg-brand rounded-full shadow-[0_0_8px_rgba(255,255,255,0.6)]"></div>
                  <h3 className="font-display uppercase tracking-widest text-sm text-white">Tickets Activos</h3>
                </div>

                {currentWeekReservations.length === 0 && (
                  <div className="glass-card p-10 text-center text-[10px] text-white/20 font-bold uppercase tracking-widest">
                    No tienes pases para esta semana
                  </div>
                )}

                {currentWeekReservations.map(res => {
                  const gym = gyms.find(g => g.id === res.gymId);
                  return (
                    <div key={res.id} className="glass-card overflow-hidden group">
                      <div className="p-6 sm:p-8 space-y-6 sm:space-y-8">
                        <div className="flex justify-between items-start">
                          <div className="flex gap-4 sm:gap-6">
                            <div className="bg-brand text-black p-4 sm:p-5 rounded-xl shadow-xl">
                              <Ticket size={24} className="sm:w-8 sm:h-8" />
                            </div>
                            <div>
                              <h4 className="font-display text-xl sm:text-3xl uppercase tracking-tight text-white leading-none mb-2">{gym?.name}</h4>
                              <span className="text-[8px] sm:text-[9px] font-bold text-brand uppercase tracking-[0.2em] bg-brand/10 px-2 py-1 rounded border border-brand/20">
                                {zones.find(z => z.id === gym?.zoneId)?.name}
                              </span>
                            </div>
                          </div>

                          {res.status === ReservationStatus.CONFIRMED && (
                            <div className="flex items-center gap-2 sm:gap-3">
                              <button
                                onClick={() => {
                                  if (gym) {
                                    setSelectedGym(gym);
                                    setSelectedChatGym(gym);
                                    setActiveTab('explore');
                                  }
                                }}
                                className="p-2 sm:p-3 text-brand hover:bg-brand/10 rounded-lg border border-brand/20 transition-all relative"
                                title="Chat con el gimnasio"
                              >
                                <MessageSquare size={18} className="sm:w-5 sm:h-5" />
                                {gym && unreadGymIds.has(gym.id) && (
                                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-dark-bg shadow-[0_0_10px_rgba(16,185,129,0.8)]"></span>
                                )}
                              </button>
                              {confirmCancelId === res.id ? (
                                <div className="flex items-center gap-1 sm:gap-2 animate-in slide-in-from-right-4">
                                  <button onClick={() => setConfirmCancelId(null)} className="p-2 sm:p-3 text-white/40 hover:text-white bg-white/5 rounded-lg">
                                    <X size={16} className="sm:w-5 sm:h-5" />
                                  </button>
                                  <button
                                    onClick={() => executeCancel(res.id)}
                                    className="bg-red-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-none font-display uppercase text-[9px] sm:text-[10px] tracking-widest"
                                  >
                                    OK
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmCancelId(res.id)}
                                  className="text-[8px] sm:text-[10px] font-display text-white/20 hover:text-red-400 transition-colors uppercase tracking-widest py-2 px-3 sm:px-4 border border-white/5 hover:border-red-400/30 rounded-lg"
                                >
                                  Anular
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 sm:gap-8 py-6 sm:py-8 border-y border-white/5">
                          <div>
                            <p className="text-[8px] sm:text-[9px] font-bold text-white/20 uppercase tracking-[0.2em] mb-2">Fecha</p>
                            <p className="text-base sm:text-xl font-display uppercase text-white">{res.date.split('-').reverse().join('/')}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[8px] sm:text-[9px] font-bold text-white/20 uppercase tracking-[0.2em] mb-2">Code</p>
                            <p className="text-base sm:text-xl font-mono font-bold text-brand tracking-tighter">{res.qrCode}</p>
                          </div>
                        </div>

                        {res.status === ReservationStatus.CONFIRMED ? (
                          <div className="flex flex-col items-center py-2 sm:py-4">
                            <div className="bg-white p-4 sm:p-8 rounded-2xl mb-4 sm:mb-6 shadow-[0_0_40px_rgba(255,255,255,0.1)]">
                              <QRCodeCanvas value={res.qrCode || res.id} size={160} level="H" includeMargin={true} className="sm:w-[200px] sm:h-[200px]" />
                            </div>
                            <p className="text-[8px] sm:text-[10px] font-bold text-white/30 uppercase tracking-[0.3em]">Escanea en recepción</p>
                          </div>
                        ) : (
                          <div className="bg-brand p-6 sm:p-8 rounded-none flex items-center justify-center gap-3 sm:gap-4 text-black font-display text-lg sm:text-xl uppercase tracking-tight shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                            <CheckCircle size={24} className="sm:w-7 sm:h-7" /> Ingreso Registrado
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-6 pt-6">
                <h3 className="font-display uppercase tracking-widest text-sm text-white/40 ml-2 flex items-center gap-3">
                  <History size={16} /> Historial
                </h3>
                <div className="grid gap-4">
                  {allUserReservations
                    .filter(r => r.status === ReservationStatus.CANCELLED || new Date(r.date + 'T00:00:00') < startOfWeek)
                    .map(res => {
                      const gym = gyms.find(g => g.id === res.gymId);
                      return (
                        <div key={res.id} className="glass-card p-6 flex items-center justify-between border-white/5 opacity-60 hover:opacity-100 transition-opacity">
                          <div className="flex items-center gap-6">
                            <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-white/20">
                              <Building2 size={24} />
                            </div>
                            <div>
                              <p className="text-lg font-display uppercase tracking-tight text-white">{gym?.name}</p>
                              <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest mt-1">{res.date}</p>
                            </div>
                          </div>
                          <span className={`text-[9px] font-bold px-3 py-1.5 rounded uppercase tracking-widest ${res.status === ReservationStatus.CANCELLED ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                            {res.status === ReservationStatus.CANCELLED ? 'Cancelado' : 'Consumido'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <LegalFooter />
    </div>
  );
};

export default ClientPortal;